import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Typography,
} from '@mui/material';
import { JSX, useEffect, useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useValidatedForm } from '../../../library/formValidation';
import { useApiFormError } from '../../../library/useApiFormError';
import { IClinicMember } from '../../../library/models/clinic';
import { memberFullName } from '../../../library/membershipDisplay';
import {
    CatalogTypeChoice,
    IServiceCatalog,
    assignableTypeIds,
    catalogTypeChoices,
} from '../../../library/models/serviceCatalog';
import ResourcesIds from '../../../resources/ResourcesIds';
import { createTherapistServicesSchema, TherapistServicesFormValues } from './therapistServicesSchema';
import { useIsCompact } from '../../../library/useIsCompact';
import { useReactMutation, useReactQuery } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import {
    choiceContextStyles,
    choicesBoxStyles,
    descriptionStyles,
    dialogActionsStyles,
    dialogTitleStyles,
    formAlertStyles,
    loadingBoxStyles,
} from './therapistServicesDialog.styles';

interface TherapistServicesDialogProps {
    /** The therapist being edited, or null when the dialog is closed. */
    therapist: IClinicMember | null;
    onClose: () => void;
}

/**
 * Which of the clinic's services a therapist performs.
 *
 * This is what the booking wizard filters on: pick a service, and the therapists offered
 * are the ones ticked here for the discipline it belongs to. A therapist with nothing
 * ticked is never offered, which is a real state -- somebody who has just joined -- rather
 * than an error.
 *
 * The choices are the clinic's disciplines -- one entry each, not one per row of the price
 * list. A subscription resolves to the discipline of the session it is a block of, so
 * ticking the service is what makes the therapist offered for the subscriptions built on it;
 * there is deliberately no separate tickbox for those. A clinic that has published no price
 * list has nothing to assign and is told so instead of being shown an empty box.
 */
export const TherapistServicesDialog = (props: TherapistServicesDialogProps): JSX.Element => {
    const { i18n, t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const isCompact = useIsCompact();

    const isOpen: boolean = props.therapist !== null;
    const therapistId: number | undefined = props.therapist?.id;

    const { data: catalogData, isLoading: isLoadingCatalog } = useReactQuery<IServiceCatalog>(
        services.serviceCatalog.getServiceCatalog(),
        { enabled: isOpen }
    );

    const { data: assignedData, isLoading: isLoadingAssigned } = useReactQuery<number[]>(
        services.therapists.getCatalogTypes(therapistId ?? 0),
        { enabled: isOpen && therapistId !== undefined }
    );

    const saveCatalogTypes = useReactMutation(services.therapists.saveCatalogTypesMutation());

    const schema = useMemo(() => createTherapistServicesSchema(translate), [translate]);

    /** The ticked ids, held by the form so the dialog can be cancelled without saving. */
    const {
        control, handleSubmit, reset, setError, formState: { isDirty },
    } = useValidatedForm<TherapistServicesFormValues>(schema, {
        defaultValues: { catalogTypeIds: [] },
    });

    const knownFields: string[] = useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<TherapistServicesFormValues>(
        setError, knownFields, translate, ResourcesIds.THERAPIST_SERVICES_ERROR);

    const choices: CatalogTypeChoice[] = useMemo(
        () => catalogTypeChoices(catalogData, i18n.language),
        [catalogData, i18n.language]
    );

    /** What the list can show, for narrowing the stored mapping to it. */
    const assignable: Set<number> = useMemo(
        () => assignableTypeIds(catalogData),
        [catalogData]
    );

    // Seeded from the server every time the dialog opens on somebody, so reopening it
    // after a cancel shows what is stored rather than the abandoned edit.
    //
    // Narrowed to the assignable ids on the way in. A mapping stored against a plan's own
    // type is unreachable now that this list is one entry per discipline -- changelog 031
    // folds the ones clinics already had, and the next save of this therapist prunes any
    // that arrive afterwards -- and seeding one would make the form dirty the moment it
    // opened, then send it back untouched.
    useEffect(() => {
        if (isOpen && assignedData !== undefined) {
            reset({ catalogTypeIds: assignedData.filter(id => assignable.has(id)) });
        }
    }, [isOpen, assignedData, assignable, reset]);

    useEffect(() => {
        if (isOpen) {
            clear();
        }
    }, [isOpen, clear]);

    const isLoading: boolean = isLoadingCatalog || isLoadingAssigned;

    const onValid = (values: TherapistServicesFormValues): void => {
        if (therapistId === undefined) {
            return;
        }

        clear();

        saveCatalogTypes.mutate(
            { therapistId, catalogTypeIds: values.catalogTypeIds },
            {
                onSuccess: () => {
                    showSnackbar(translate(ResourcesIds.THERAPIST_SERVICES_SUCCESS), 'success');
                    props.onClose();
                },
                onError: report,
            }
        );
    };

    return (
        <Dialog open={isOpen} onClose={props.onClose} maxWidth="sm" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(ResourcesIds.THERAPIST_SERVICES_TITLE)}
            </DialogTitle>

            <DialogContent>
                {formError && (
                    <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>
                )}

                <Typography variant="body2" sx={descriptionStyles}>
                    {translate(ResourcesIds.THERAPIST_SERVICES_DESCRIPTION, {
                        name: props.therapist ? memberFullName(props.therapist) : '',
                    })}
                </Typography>

                {isLoading ? (
                    <Box sx={loadingBoxStyles}>
                        <CircularProgress size={40} />
                    </Box>
                ) : choices.length === 0 ? (
                    <Alert severity="info">
                        {translate(ResourcesIds.THERAPIST_SERVICES_NO_CATALOG)}
                    </Alert>
                ) : (
                    <Box sx={choicesBoxStyles}>
                        {choices.map(choice => (
                            <FormControlLabel
                                key={choice.id}
                                control={
                                    // A tick set is one form value, so it goes through a
                                    // Controller rather than being registered field by field.
                                    <Controller
                                        name="catalogTypeIds"
                                        control={control}
                                        render={({ field }) => (
                                            <Checkbox
                                                checked={field.value.includes(choice.id)}
                                                onChange={() => field.onChange(
                                                    field.value.includes(choice.id)
                                                        ? field.value.filter(id => id !== choice.id)
                                                        : [...field.value, choice.id]
                                                )}
                                            />
                                        )}
                                    />
                                }
                                label={
                                    <>
                                        {choice.name}
                                        <Typography component="span" variant="caption" sx={choiceContextStyles}>
                                            {choice.context}
                                        </Typography>
                                    </>
                                }
                            />
                        ))}
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={dialogActionsStyles}>
                <Button onClick={props.onClose} variant="outlined">
                    {translate(ResourcesIds.CANCEL_TEXT)}
                </Button>
                <Button
                    onClick={handleSubmit(onValid)}
                    variant="contained"
                    disabled={!isDirty || isLoading || saveCatalogTypes.isPending}
                >
                    {translate(ResourcesIds.SERVICES_SAVE_ACTION)}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
